/** Local-first persistence: scene collections in IndexedDB, media assets in OPFS (IndexedDB fallback). */
import type { Project } from '../core/model';
import type { AssetResolver } from '../core/services';

export interface ProjectSummary {
  id: string;
  name: string;
  updatedAt: number;
  sceneCount: number;
}

export interface ProjectRepository {
  list(): Promise<ProjectSummary[]>;
  /** Returns a migrated, validated project or null if it does not exist. */
  load(id: string): Promise<Project | null>;
  save(project: Project): Promise<void>;
  delete(id: string): Promise<void>;
  /** Id of the collection that was open last, to restore on startup. */
  getLastOpenedId(): Promise<string | null>;
  setLastOpenedId(id: string | null): Promise<void>;
}

export interface AssetMeta {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: number;
}

export interface AssetPutOptions {
  /** Reject larger files with AssetImportError 'too-large' before reading them. */
  maxBytes?: number;
  /** Aborting rejects with a DOMException named 'AbortError' and stores nothing. */
  signal?: AbortSignal;
  /** 0..1 while the file is read and hashed. */
  onProgress?(fraction: number): void;
}

export type AssetImportErrorCode = 'too-large' | 'unsupported' | 'storage-full';

/** A file that cannot be imported, with a user-presentable message. */
export class AssetImportError extends Error {
  constructor(
    readonly code: AssetImportErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'AssetImportError';
  }
}

export interface AssetStore extends AssetResolver {
  /**
   * Stores the file and returns its metadata. Identical content may be
   * de-duplicated. Hashing is streamed, so the file is never held in memory
   * whole. An empty `file.type` is replaced by the type sniffed from the bytes.
   * Rejects with AssetImportError for files that cannot be stored.
   */
  put(file: Blob, name: string, options?: AssetPutOptions): Promise<AssetMeta>;
  getMeta(assetId: string): Promise<AssetMeta | null>;
  list(): Promise<AssetMeta[]>;
  delete(assetId: string): Promise<void>;
  /** Revokes cached object URLs. */
  dispose(): void;
}

/**
 * Portable export: a single file containing the project JSON and, optionally,
 * every asset it references.
 */
export interface ProjectBundler {
  exportBundle(project: Project, options: { includeAssets: boolean }): Promise<Blob>;
  /** Imports assets into the store and returns the project with a fresh id. Throws on invalid input. */
  importBundle(file: Blob): Promise<Project>;
}

/**
 * Upgrade any previously saved document to the current schema and validate its
 * structure. Throws a descriptive Error for data that cannot be repaired.
 */
export type ProjectMigrator = (raw: unknown) => Project;
